<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core';
import { onMounted, onUpdated, nextTick, computed, ref, watch } from 'vue'
import { SurpriseBagCard } from '../surpriseBagCard';
import type { SurpriseBag, SurpriseBagTranslations } from '@pkg/types/surpriseBag';

const props = defineProps<{
  items?: SurpriseBag[];
  staticTranslations: SurpriseBagTranslations;
}>();

const emit = defineEmits<{
  'item-clicked': [bag: SurpriseBag];
}>();

const visibleItems = ref<Boolean[]>([]);
const cardRefs = ref<HTMLElement[]>([]);
const articles = computed(() => props.items ?? []);

const observeCards = () => {
  cardRefs.value.forEach((card, index) => {
    if (!card) return;
    
    useIntersectionObserver(card, ([{ isIntersecting }]) => {
      if (isIntersecting) {
        visibleItems.value[index] = true;
      }
    });
  });
};

watch(articles, (newArticles) => {
  visibleItems.value = new Array(newArticles.length).fill(false);
  nextTick(() => observeCards());
}, { deep: true });

onMounted(async() => {
  nextTick(() => observeCards());
});

onUpdated(() => {
  nextTick(() => observeCards());
});

</script>

<template>
  <div class="virtual-list__wrapper">
    <div :key="item?.item?.itemId || index" :id="item?.item?.itemId || index.toString()" ref="cardRefs" v-for="(item, index) in articles" class="virtual-list__item">
      <SurpriseBagCard v-if="visibleItems[index]" :article="item" :static-translations="staticTranslations" @item-clicked="emit('item-clicked', $event)"/>
      <SurpriseBagCard v-else />
    </div>
  </div>
</template>

<style scoped>
@import '../../../assets/css/mixins/_mixins.index.pcss';

.virtual-list {
  &__wrapper {
    width: auto;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem;
    overflow: auto;
    justify-items: center;
    width: 100%;

    @screen tablet-wide {
      min-width: 696px;
      grid-template-columns: repeat(2, 1fr);
    }
    @screen desk {
      min-width: 1052px;
      grid-template-columns: repeat(3, 1fr);
    }
    @screen big-desk {
      justify-items: left;
    }
  }

  &__item {
    width: auto;
  }
}
</style>